import {apiClient, FetcherSettings} from './apiClient';
import {IpDetails, OldShortLink, SDKApiKeyResponse, ShortLink} from '../utils/types';
import {GuestAccount, Participant} from '../utils';

export type UserInitiateResponse = {
  session_id: string;
};

export type UserInitiatePayload = {
  email: string;
  password: string;
};

export type UserFinalizePayload = {
  session_id: string;
  code: string;
};

export type UserFinalizeResponse = {
  access_token: string;
};

export type OAuthCallbackParams = {
  code: string;
  state: string;
};

class AuthApi {
  static ENDPOINTS = {
    refresh: () => `api/v3/checkin-online-token/refresh/`,
    apikeyAuth: () => `sdk/api/v1/apikey-auth`,
    shortLink: (token: string) => `api/short-link/${token}/`,
    ipDetails: () => `api/v3/get-country-by-ip/`,
    usersInitiate: () => `api/v1/guest-accounts/users/initiate/`,
    usersFinalize: () => `api/v1/guest-accounts/users/finalize/`,
    guestToken: () => `api/v1/guest-accounts/token/`,
    refreshGuestToken: () => `api/v1/guest-accounts/token/renew/`,
    me: () => `api/v1/guest-accounts/users/me/`,
    googleCallback: () => `api/v1/guest-accounts/oauth/google/callback/`,
    participants: (id = '') =>
      `api/v1/guest-accounts/participants/${id ? `?id=${id}` : ''}`,
  };

  async getAuthToken(payload: {apikey: string}, fetcherSettings?: FetcherSettings) {
    const response = await apiClient.post<SDKApiKeyResponse>(
      AuthApi.ENDPOINTS.apikeyAuth(),
      {
        json: payload,
        publicAccess: true,
        ...fetcherSettings,
      },
    );
    return response.body;
  }

  getChekinToken({token}: {token: string}, fetcherSettings?: FetcherSettings) {
    return apiClient.get<OldShortLink | ShortLink>(AuthApi.ENDPOINTS.shortLink(token), {
      publicAccess: true,
      ...fetcherSettings,
    });
  }

  async fetchIpDetails(fetcherSettings?: FetcherSettings) {
    const response = await apiClient.get<IpDetails>(
      AuthApi.ENDPOINTS.ipDetails(),
      fetcherSettings,
    );

    return response.body;
  }

  async initiateUser(payload: UserInitiatePayload, fetcherSettings?: FetcherSettings) {
    const response = await apiClient.post<UserInitiateResponse>(
      AuthApi.ENDPOINTS.usersInitiate(),
      {
        json: payload,
        publicAccess: true,
        ...fetcherSettings,
      },
    );
    return response.body;
  }

  async finalizeUser(payload: UserFinalizePayload, fetcherSettings?: FetcherSettings) {
    const response = await apiClient.post<UserFinalizeResponse>(
      AuthApi.ENDPOINTS.usersFinalize(),
      {
        json: payload,
        publicAccess: true,
        ...fetcherSettings,
      },
    );
    return response.body;
  }

  async getToken(payload: UserInitiatePayload, fetcherSettings?: FetcherSettings) {
    const response = await apiClient.post<UserFinalizeResponse>(
      AuthApi.ENDPOINTS.guestToken(),
      {json: payload, publicAccess: true, ...fetcherSettings},
    );
    return response.body;
  }

  async getMe(fetcherSettings?: FetcherSettings) {
    const response = await apiClient.get<GuestAccount>(
      AuthApi.ENDPOINTS.me(),
      fetcherSettings,
    );
    return response.body;
  }

  async getParticipants(fetcherSettings?: FetcherSettings) {
    const response = await apiClient.get<Participant[]>(
      AuthApi.ENDPOINTS.participants(),
      fetcherSettings,
    );
    return response.body;
  }

  async oauthGoogleCallback(
    payload: OAuthCallbackParams,
    fetcherSettings?: FetcherSettings & {headers?: HeadersInit},
  ) {
    const response = await apiClient.post<UserFinalizeResponse>(
      AuthApi.ENDPOINTS.googleCallback(),
      {
        json: payload,
        publicAccess: true,
        ...fetcherSettings,
      },
    );
    return response.body;
  }
}

export {AuthApi};
